/**
 * Turning a photo into the file a slot stores: the right shape, no bigger than
 * it needs to be, upright, WebP, and under the upload limit.
 *
 * Two halves. The geometry at the top is pure - no DOM, no decoding - so the
 * decisions that matter (where the on-screen guide lands in the sensor's
 * pixels, how far a photo is scaled, when it is too small to look good) are
 * unit-tested directly. The canvas work below it is the part a browser has to
 * do, and like `toWebp` it is a courtesy rather than a gate: a browser that
 * cannot decode or encode hands back the original file, and so does jsdom.
 *
 * The one refusal is size. A file over `maxFileBytes` would be turned away by
 * the server after the merchant had waited for the whole upload, so that is
 * raised here as `ImageTooLargeError`, while there is still time to say why.
 */

import { centredRect, clampRect, withAspect } from '@/lib/cropRect';
import type { CropRect, ImageDimensions } from '@/lib/imageEdit';
import {
  IMAGE_SPECS,
  MAX_UPLOAD_BYTES,
  aspectValue,
  type AspectRatio,
  type ImageSpec,
} from '@/lib/imageSpecs';
import { isAnimatedGif, WEBP_TYPE, webpName } from '@/lib/toWebp';

export type ResolutionWarning = 'low_resolution';

export interface ProcessedImage {
  file: File;
  /** Pixel size of `file`; 0 when the browser could not decode it to tell. */
  width: number;
  height: number;
  warning: ResolutionWarning | null;
}

export interface ProcessImageOptions {
  /**
   * A region in the source's own (upright) pixels, `'centre'` for the largest
   * centred rectangle of the spec's aspect, or `'none'` to keep the whole
   * picture. Defaults to `'centre'`.
   */
  crop?: CropRect | 'centre' | 'none';
  /** The name the stored file is based on; the extension follows the bytes. */
  name?: string;
}

/** The finished file is still over the upload limit after every reduction. */
export class ImageTooLargeError extends Error {
  readonly bytes: number;
  readonly maxBytes: number;

  constructor(bytes: number, maxBytes: number) {
    super('The image is too large to upload.');
    this.name = 'ImageTooLargeError';
    this.bytes = bytes;
    this.maxBytes = maxBytes;
  }
}

// --- Geometry --------------------------------------------------------------

/** The largest centred rectangle of `aspect` inside the source. */
export function frameRect(source: ImageDimensions, aspect: AspectRatio | null): CropRect {
  return centredRect(source, aspectValue(aspect));
}

/**
 * Where the framing guide sits inside a viewfinder of `viewport` size: the
 * largest rectangle of `aspect` that leaves a `padding` margin (a fraction of
 * the viewport's short edge) all round. Not rounded - it is drawn in CSS
 * pixels and mapped onto the sensor, and rounding twice would drift.
 */
export function guideRect(
  viewport: ImageDimensions,
  aspect: AspectRatio | null,
  padding = 0.06,
): CropRect {
  const margin = Math.min(viewport.width, viewport.height) * padding;
  const room = {
    width: Math.max(0, viewport.width - margin * 2),
    height: Math.max(0, viewport.height - margin * 2),
  };
  const ratio = aspectValue(aspect);
  let width = room.width;
  let height = room.height;
  if (ratio) {
    height = width / ratio;
    if (height > room.height) {
      height = room.height;
      width = height * ratio;
    }
  }
  return {
    x: (viewport.width - width) / 2,
    y: (viewport.height - height) / 2,
    width,
    height,
  };
}

/**
 * Maps the guide drawn over the live video to the same region of the frame
 * the camera actually delivered.
 *
 * The video is shown `object-cover`: scaled until it fills the viewfinder on
 * both axes and centred, so part of it is off-screen. The guide is measured in
 * viewfinder pixels; undoing that scale and offset gives sensor pixels, which
 * is what "what you framed is what you get" requires. The result is locked
 * back to `aspect` after rounding so a 3:1 guide cannot become 3.002:1.
 */
export function viewfinderToSourceRect(
  source: ImageDimensions,
  viewport: ImageDimensions,
  guide: CropRect,
  aspect: AspectRatio | null = null,
): CropRect {
  if (viewport.width <= 0 || viewport.height <= 0 || source.width <= 0 || source.height <= 0) {
    return frameRect(source, aspect);
  }
  const scale = Math.max(viewport.width / source.width, viewport.height / source.height);
  const offsetX = (viewport.width - source.width * scale) / 2;
  const offsetY = (viewport.height - source.height * scale) / 2;
  const rect = {
    x: (guide.x - offsetX) / scale,
    y: (guide.y - offsetY) / scale,
    width: guide.width / scale,
    height: guide.height / scale,
  };
  return withAspect(clampRect(rect, source), aspectValue(aspect), source);
}

/**
 * The stored size for a crop: its own shape, the longest edge capped at the
 * spec's ceiling, and never enlarged - upscaling adds bytes, not detail.
 */
export function outputSize(crop: ImageDimensions, spec: ImageSpec): ImageDimensions {
  const longest = Math.max(crop.width, crop.height);
  if (longest <= spec.maxEdge) {
    return { width: Math.round(crop.width), height: Math.round(crop.height) };
  }
  const scale = spec.maxEdge / longest;
  return {
    width: Math.max(1, Math.round(crop.width * scale)),
    height: Math.max(1, Math.round(crop.height * scale)),
  };
}

/**
 * Whether the crop has fewer pixels than the slot needs to look sharp.
 *
 * A warning, not a refusal: a merchant with one old photo of a product still
 * needs to sell it. A free-shape slot has no fixed width and height to hold a
 * crop to, so only its short edge is compared, against the smaller minimum.
 */
export function resolutionWarning(crop: ImageDimensions, spec: ImageSpec): ResolutionWarning | null {
  if (!spec.aspect) {
    return Math.min(crop.width, crop.height) < Math.min(spec.minWidth, spec.minHeight)
      ? 'low_resolution'
      : null;
  }
  return crop.width < spec.minWidth || crop.height < spec.minHeight ? 'low_resolution' : null;
}

// --- Canvas pipeline -------------------------------------------------------

/** Tried in order until the file fits; the first is close to the source. */
const QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6] as const;
/** After the lowest quality, each retry is this fraction of the last size. */
const SHRINK_FACTOR = 0.8;
const MAX_SHRINKS = 5;

/** Formats a canvas that will not write WebP may fall back to. */
const JPEG_TYPE = 'image/jpeg';
const PNG_TYPE = 'image/png';

/** Sources that may carry transparency, which a JPEG would paint black. */
const MAY_BE_TRANSPARENT = new Set([PNG_TYPE, 'image/gif', WEBP_TYPE, 'image/avif']);

/** Used when a caller has no slot in mind: shape untouched, generous ceiling. */
const PLAIN_UPLOAD: ImageSpec = {
  ...IMAGE_SPECS.AI_SOURCE,
  aspectChoices: undefined,
  maxEdge: 4096,
  minWidth: 0,
  minHeight: 0,
  maxFileBytes: MAX_UPLOAD_BYTES,
};

function canvasAvailable(): boolean {
  return typeof createImageBitmap === 'function' && typeof document !== 'undefined';
}

/**
 * Decodes with the EXIF orientation applied, so a phone photo taken sideways
 * is stored the way it was held. Older engines reject the options bag, and
 * they are also the ones that never honoured orientation, so a plain decode
 * is the right second try.
 */
async function decode(source: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(source, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(source);
  }
}

function asFile(source: Blob, name: string): File {
  return source instanceof File ? source : new File([source], name, { type: source.type });
}

function renamed(name: string, type: string): string {
  if (type === WEBP_TYPE) return webpName(name);
  const base = name.replace(/\.[^.]+$/, '').trim() || 'image';
  return `${base}.${type === JPEG_TYPE ? 'jpg' : 'png'}`;
}

function resolveCrop(
  crop: ProcessImageOptions['crop'],
  bounds: ImageDimensions,
  aspect: AspectRatio | null,
): CropRect {
  if (crop === 'none') return { x: 0, y: 0, width: bounds.width, height: bounds.height };
  if (crop === undefined || crop === 'centre') return frameRect(bounds, aspect);
  return clampRect(crop, bounds);
}

function paint(bitmap: ImageBitmap, crop: CropRect, size: ImageDimensions): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext('2d');
  if (!context || typeof canvas.toBlob !== 'function') return null;
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, size.width, size.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

interface Encoded {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Paints and encodes, stepping quality down and then size down until the
 * bytes fit. `null` means the canvas could not be used at all.
 *
 * WebP is asked for first. A canvas that cannot write it answers with a PNG
 * instead of refusing (Safari does this), so the type that comes back decides:
 * from then on a photo is written as JPEG, which keeps it small, and anything
 * that may be transparent as PNG, which keeps the transparency.
 */
async function encodeWithinBudget(
  bitmap: ImageBitmap,
  crop: CropRect,
  size: ImageDimensions,
  sourceType: string,
  maxBytes: number,
  originalWillDo: boolean,
): Promise<Encoded | 'original' | null> {
  let type = WEBP_TYPE;
  let target = size;
  let shrinks = 0;
  let lastBytes = 0;

  for (;;) {
    const canvas = paint(bitmap, crop, target);
    if (!canvas) return null;

    let switched = false;
    for (const quality of type === PNG_TYPE ? [1] : QUALITY_STEPS) {
      const blob = await toBlob(canvas, type, quality);
      if (!blob) return null;
      if (type === WEBP_TYPE && blob.type !== WEBP_TYPE) {
        // Re-encoding an untouched photo into another legacy format gains
        // nothing, so the caller keeps the original when it already fits.
        if (originalWillDo) return 'original';
        type = MAY_BE_TRANSPARENT.has(sourceType) ? PNG_TYPE : JPEG_TYPE;
        switched = true;
        break;
      }
      lastBytes = blob.size;
      if (blob.size <= maxBytes) return { blob, ...target };
    }
    // A format switch retries the same size before anything is shrunk.
    if (switched) continue;

    if (shrinks === MAX_SHRINKS) throw new ImageTooLargeError(lastBytes, maxBytes);
    shrinks += 1;
    target = {
      width: Math.max(1, Math.round(target.width * SHRINK_FACTOR)),
      height: Math.max(1, Math.round(target.height * SHRINK_FACTOR)),
    };
  }
}

/** The untouched original, provided the server would accept it. */
function keepOriginal(file: File, spec: ImageSpec, size?: ImageDimensions): ProcessedImage {
  if (file.size > spec.maxFileBytes) throw new ImageTooLargeError(file.size, spec.maxFileBytes);
  return { file, width: size?.width ?? 0, height: size?.height ?? 0, warning: null };
}

async function animatedGif(source: Blob): Promise<boolean> {
  if (source.type !== 'image/gif') return false;
  try {
    return await isAnimatedGif(source);
  } catch {
    return false;
  }
}

/**
 * Crops, downscales and encodes one image for `spec`.
 *
 * Decode and encode failures fall back to the original file - a photo the
 * merchant cannot store is worse than one stored uncropped - and an animated
 * GIF is always passed through, since a canvas would keep only its first
 * frame. Throws only `ImageTooLargeError`.
 */
export async function processImage(
  source: Blob,
  spec: ImageSpec,
  options: ProcessImageOptions = {},
): Promise<ProcessedImage> {
  const name = options.name ?? (source instanceof File ? source.name : 'photo');
  const original = asFile(source, name);

  if (!source.type.startsWith('image/')) return keepOriginal(original, spec);
  if (await animatedGif(source)) return keepOriginal(original, spec);
  if (!canvasAvailable()) return keepOriginal(original, spec);

  let bitmap: ImageBitmap;
  try {
    bitmap = await decode(source);
  } catch {
    return keepOriginal(original, spec);
  }

  try {
    const bounds = { width: bitmap.width, height: bitmap.height };
    const crop = resolveCrop(options.crop, bounds, spec.aspect);
    const size = outputSize(crop, spec);
    const warning = resolutionWarning(crop, spec);
    const untouched =
      crop.x === 0 &&
      crop.y === 0 &&
      size.width === bounds.width &&
      size.height === bounds.height;

    // Already the format of record, already the right size: re-encoding would
    // only cost quality.
    if (untouched && source.type === WEBP_TYPE && source.size <= spec.maxFileBytes) {
      return { file: original, ...bounds, warning };
    }

    let encoded: Encoded | 'original' | null;
    try {
      encoded = await encodeWithinBudget(
        bitmap,
        crop,
        size,
        source.type,
        spec.maxFileBytes,
        untouched && source.size <= spec.maxFileBytes,
      );
    } catch (error) {
      if (error instanceof ImageTooLargeError) throw error;
      return keepOriginal(original, spec, bounds);
    }
    if (encoded === null || encoded === 'original') {
      return { ...keepOriginal(original, spec, bounds), warning };
    }

    return {
      file: new File([encoded.blob], renamed(name, encoded.blob.type), {
        type: encoded.blob.type,
        lastModified: original.lastModified,
      }),
      width: encoded.width,
      height: encoded.height,
      warning,
    };
  } finally {
    bitmap.close?.();
  }
}

/**
 * The one step every plain upload goes through before it is sent: upright,
 * no larger than the slot's ceiling, WebP, and under the size limit - but
 * never cropped, since a file picked from the device keeps the shape the
 * merchant chose (the edit dialog is where they reshape it).
 *
 * Never throws for a file the browser cannot process; it sends the original,
 * as `toWebpFile` always did. Throws `ImageTooLargeError` when what would be
 * sent is over the limit, so the caller can say so instead of uploading it.
 */
export async function prepareImageForUpload(file: File, spec: ImageSpec = PLAIN_UPLOAD): Promise<File> {
  const processed = await processImage(file, spec, { crop: 'none', name: file.name });
  return processed.file;
}
